const FOCUSABLE_SELECTOR = [
	'a[href]',
	'button:not([disabled])',
	'input:not([disabled]):not([type="hidden"])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'[tabindex]:not([tabindex="-1"])',
].join(',');

const getFocusableElements = (container) => Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR))
	.filter((element) => {
		const style = window.getComputedStyle(element);

		return !element.hasAttribute('inert') && style.display !== 'none' && style.visibility !== 'hidden';
	});

const getInertTargets = (dialog) => {
	const targets = new Set();
	let current = dialog;

	while (current && current !== document.body) {
		Array.from(current.parentElement?.children || []).forEach((sibling) => {
			if (sibling !== current && !sibling.contains(dialog)) {
				targets.add(sibling);
			}
		});

		current = current.parentElement;
	}

	return Array.from(targets).filter((target) => !target.matches('script, style, link'));
};

export const createModalA11y = (dialog, options = {}) => {
	if (!dialog) return null;

	let previouslyFocusedElement = null;
	let inertTargets = [];
	const initialFocus = options.initialFocus || null;
	const restoreFocus = options.restoreFocus !== false;
	const resolveInertTargets = options.getInertTargets || getInertTargets;
	const excludeInertTargets = options.excludeInertTargets || (() => false);

	const focusDialog = () => {
		const focusTarget = typeof initialFocus === 'function' ? initialFocus() : initialFocus;
		const target = focusTarget || getFocusableElements(dialog)[0] || dialog;

		if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');

		target.focus({ preventScroll: true });
	};

	const handleKeydown = (event) => {
		if (event.key !== 'Tab') return;

		const focusableElements = getFocusableElements(dialog);

		if (!focusableElements.length) {
			event.preventDefault();
			dialog.focus({ preventScroll: true });
			return;
		}

		const firstElement = focusableElements[0];
		const lastElement = focusableElements[focusableElements.length - 1];

		if (event.shiftKey && document.activeElement === firstElement) {
			event.preventDefault();
			lastElement.focus();
		}

		if (!event.shiftKey && document.activeElement === lastElement) {
			event.preventDefault();
			firstElement.focus();
		}
	};

	const handleFocusin = (event) => {
		if (dialog.contains(event.target)) return;

		focusDialog();
	};

	const activate = () => {
		previouslyFocusedElement = document.activeElement;
		inertTargets = resolveInertTargets(dialog).filter((target) => !excludeInertTargets(target));
		inertTargets.forEach((target) => target.setAttribute('inert', ''));

		document.addEventListener('keydown', handleKeydown);
		document.addEventListener('focusin', handleFocusin);

		requestAnimationFrame(focusDialog);
	};

	const deactivate = () => {
		document.removeEventListener('keydown', handleKeydown);
		document.removeEventListener('focusin', handleFocusin);

		inertTargets.forEach((target) => target.removeAttribute('inert'));
		inertTargets = [];

		if (restoreFocus && previouslyFocusedElement && typeof previouslyFocusedElement.focus === 'function') {
			previouslyFocusedElement.focus({ preventScroll: true });
		}

		previouslyFocusedElement = null;
	};

	return {
		activate,
		deactivate,
		focus: focusDialog,
	};
};

window.createModalA11y = window.createModalA11y || createModalA11y;
